import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Badge } from '@capawesome/capacitor-badge';
import { getActivePortal, getUserData, isAuthenticated } from '@/utils/auth';
import * as SubjectContext from '../context/SubjectChapterContext';
import { soundService } from '../services/soundService';
import { registerPushToken, getUnreadCount, reportFCMReceipt } from '@/api/notifications';

// Module-level singleton flags to prevent race conditions across all hook instances
let isPushInitialized = false;
let isSetupInProgress = false;
let lastRegisteredUser = null;
let lastSessionToken = null;
let lastHasSelection = false; // Track selection state to allow re-triggering

// Global state for unread count to sync across all hook instances (Sidebar, Layout, TopBar)
let globalUnreadCount = 0;
const unreadCountListeners = new Set();

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const getDeclineKey = (portal, userId) => `${portal || 'general'}_${userId || 'anon'}_notification_soft_prompt_declined`;
const getPromptTimeKey = (portal, userId) => `${portal || 'general'}_${userId || 'anon'}_notification_soft_prompt_time`;

const isTuitionNotification = (data) => {
  if (!data) return false;
  if (data.sound_type === 'tuition_bell') return true;
  const type = data.type || '';
  return [
    'live_class_booking',
    'live_class_cancelled',
    'live_class_reschedule',
    'live_class_cancellation',
    'live_class_credit_returned'
  ].includes(type);
};

export const useNotifications = (isMain = false) => {
  const navigate = useNavigate();
  const isPrimary = isMain === true;
  const [unreadCount, setUnreadCount] = useState(globalUnreadCount);
  const [active, setActive] = useState(true);
  const [showConsentModal, setShowConsentModal] = useState(false);

  const portal = getActivePortal();
  const authed = isAuthenticated(portal);
  const user = getUserData(portal);
  const userId = user?.id || user?.user_id;

  // Sync with global unread count
  useEffect(() => {
    // 1. Immediate Sync
    if (unreadCount !== globalUnreadCount) {
      setUnreadCount(globalUnreadCount);
    }

    // 2. Listener
    const listener = (count) => setUnreadCount(count);
    unreadCountListeners.add(listener);
    return () => unreadCountListeners.delete(listener);
  }, [unreadCount]);

  // Listen for background notifications forwarded from Service Worker
  useEffect(() => {
    if (!isPrimary || typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    const handleSwMessage = (event) => {
      if (event.data?.type === 'PLAY_TUITION_BELL') {
        console.log('[useNotifications] 🔔 Received PLAY_TUITION_BELL from service worker');
        soundService?.playTuitionBellSound();
        fetchUnreadCount();
      } else if (event.data?.type === 'PLAY_CHIME_SOUND') {
        console.log('[useNotifications] 🔔 Received PLAY_CHIME_SOUND from service worker');
        soundService?.playNotificationSound();
        fetchUnreadCount();
      }
    };
    navigator.serviceWorker.addEventListener('message', handleSwMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleSwMessage);
  }, [isPrimary]);

  const updateUnreadCount = async (count) => {
    globalUnreadCount = count;
    unreadCountListeners.forEach(listener => listener(count));

    // Update native app icon badge
    if (Capacitor.isNativePlatform()) {
      try {
        // Issue 1: On iOS, setting badge count can trigger permission prompt.
        // We only update if already granted.
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const perm = await PushNotifications.checkPermissions();
        if (perm?.receive === 'granted') {
          Badge.set({ count }).catch(() => { });
        }
      } catch (e) {
        // Fallback for non-push platforms or errors
        Badge.set({ count }).catch(() => { });
      }
    }

    // Update Web/PWA App Icon Badge
    if ('setAppBadge' in navigator) {
      try {
        if (count > 0) {
          navigator.setAppBadge(count).catch(() => { });
        } else {
          navigator.clearAppBadge().catch(() => { });
        }
      } catch (e) { }
    }
  };

  // Safely get context and calculate hasSelection
  const context = SubjectContext?.useSubjectChapter?.();
  const hasSelection = !!(context?.selectedSubjectId && context?.selectedChapterId);

  const contextRef = useRef({ hasSelection, active, authed, userId });
  useEffect(() => {
    contextRef.current = { hasSelection, active, authed, userId };
  }, [hasSelection, active, authed, userId]);

  const fetchUnreadCount = async () => {
    const p = getActivePortal();
    if (!isAuthenticated(p)) return;
    try {
      const response = await getUnreadCount();
      // Verified from backend/users/views.py:352 -> return Response({'unread_count': count})
      const count = response?.data?.unread_count || 0;
      updateUnreadCount(count);
    } catch (err) {
      console.warn('[Notifications] ❌ Failed to fetch unread count:', err);
    }
  };

  // 1. Refresh on window focus (great for Mac App/PWA), throttled to 5 minutes
  useEffect(() => {
    let lastUnreadFetchTimestamp = Date.now();

    const handleFocusOrVisible = () => {
      if (document.visibilityState === 'hidden') return;
      if (!contextRef.current.authed) return;
      const now = Date.now();
      // Throttle window focus and visibility re-fetches to at most once per 5 minutes
      if (now - lastUnreadFetchTimestamp < 300000) return;
      lastUnreadFetchTimestamp = now;
      fetchUnreadCount();
    };

    window.addEventListener('focus', handleFocusOrVisible);
    document.addEventListener('visibilitychange', handleFocusOrVisible);
    return () => {
      window.removeEventListener('focus', handleFocusOrVisible);
      document.removeEventListener('visibilitychange', handleFocusOrVisible);
    };
  }, []);

  const getFCMTokenWithRetry = async (FCM, retries = 5, delayMs = 1500) => {
    for (let i = 0; i < retries; i++) {
      try {
        const fcmResult = await FCM.getToken();
        if (fcmResult?.token) return fcmResult.token;
      } catch (err) {
        if (i < retries - 1) await sleep(delayMs);
      }
    }
    throw new Error('FCM token could not be retrieved');
  };

  // Issue 2: Reset singleton flags when user logs out to ensure re-sync on next login
  useEffect(() => {
    if (!authed) {
      isPushInitialized = false;
      lastRegisteredUser = null;
      localStorage.removeItem('last_registered_session_token');
    }
  }, [authed]);

  useEffect(() => {
    // Initial fetch on mount or user change
    if (authed) {
      // Issue 1: Delay initial unread count fetch on iOS if we haven't asked for permissions yet.
      // This prevents the Badge.set() from triggering the system prompt before our custom modal.
      const checkAndFetch = async () => {
        if (Capacitor.getPlatform() === 'ios') {
          const { PushNotifications } = await import('@capacitor/push-notifications');
          const perm = await PushNotifications.checkPermissions();
          if (perm?.receive === 'prompt') return; // Skip for now, setup() will handle it
        }
        fetchUnreadCount();
      };
      checkAndFetch();
    }

    const setup = async () => {
      // 🛡️ Singleton Guard: Only Primary handles Push Registration
      // Account-Aware: If the user changed (logout/login), we MUST re-register.
      const sessionToken = localStorage.getItem(`${portal}_access_token`);
      const registeredToken = localStorage.getItem('last_registered_session_token');
      const isSameSession = sessionToken && sessionToken === registeredToken;
      const isSameUser = userId === lastRegisteredUser && isSameSession;

      // If initialized but selection just happened, we must allow setup to proceed
      const currentHasSelection = contextRef.current.hasSelection;
      const selectionJustHappened = currentHasSelection && !lastHasSelection;

      if (!isPrimary || (isPushInitialized && isSameUser && !selectionJustHappened) || isSetupInProgress) {
        return;
      }

      // Context & Route Flow Detection
      const currentState = contextRef.current;
      const pathname = window.location.pathname + window.location.hash;
      const isTeacherFlow = portal === 'teacher' || pathname.includes('/teacher');
      const isStudentFlow = !isTeacherFlow && (pathname.includes('/student/learn') || pathname.includes('/student/curiosity'));

      // 2. Web / PWA Logic
      if (Capacitor.getPlatform() === 'web') {
        isSetupInProgress = true;
        try {
          const { requestForToken, onForegroundMessage } = await import('../lib/firebase');

          // If browser notification permission is in default (unprompted) state, soft prompt user first
          if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
            const declineKey = getDeclineKey(portal, currentState.userId);
            const promptTimeKey = getPromptTimeKey(portal, currentState.userId);
            const hasDeclined = localStorage.getItem(declineKey) || localStorage.getItem('notification_soft_prompt_declined');
            const lastPromptTime = localStorage.getItem(promptTimeKey) || localStorage.getItem('notification_soft_prompt_time');
            const now = Date.now();
            const threeDays = 3 * 24 * 60 * 60 * 1000;

            if (!hasDeclined || (now - parseInt(lastPromptTime || '0')) > threeDays) {
              const promptDelay = isTeacherFlow ? 2500 : 5000;
              await sleep(promptDelay);
              if (contextRef.current.authed && (isTeacherFlow || !isStudentFlow || contextRef.current.hasSelection)) {
                setShowConsentModal(true);
              }
            }
          } else {
            const token = await requestForToken();
            if (token) {
              localStorage.setItem('fcm_token', token);
              const { getBrowserFingerprint } = await import('@/utils/auth');
              const fingerprint = await getBrowserFingerprint();
              await registerPushToken(token, 'web', fingerprint);
              lastRegisteredUser = userId;
              localStorage.setItem('last_registered_session_token', localStorage.getItem(`${portal}_access_token`));
            }
          }

          onForegroundMessage((payload) => {
            console.log('[Notifications] 🔔 Notification received in foreground!', payload);
            if (contextRef.current.active) {
              const data = payload?.data || {};
              if (data.fcm_log_id) {
                reportFCMReceipt(data.fcm_log_id, 'delivered', 'web');
              }
              if (isTuitionNotification(data)) {
                soundService?.playTuitionBellSound();
              } else {
                soundService?.playNotificationSound();
              }
              fetchUnreadCount();
            }
          });

          isPushInitialized = true;
          localStorage.setItem('last_registered_session_token', localStorage.getItem(`${portal}_access_token`));
        } catch (err) {
          console.warn('[useNotifications] Web push registration failed:', err);
        } finally {
          isSetupInProgress = false;
        }
        return;
      }

      // 3. Native Platforms (iOS/Android)
      isSetupInProgress = true;

      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const { FCM } = await import('@capacitor-community/fcm');

        let perm = await PushNotifications.checkPermissions();

        if (perm?.receive === 'prompt' && isStudentFlow && !currentState.hasSelection) {
          isSetupInProgress = false;
          return;
        }

        lastRegisteredUser = currentState.userId;
        lastHasSelection = currentState.hasSelection;
        await PushNotifications.removeAllListeners();

        // Android Channel Registration
        if (Capacitor.getPlatform() === 'android') {
          try {
            await PushNotifications.createChannel({
              id: 'tuition_alerts',
              name: 'Tuition & Class Alerts',
              description: 'Urgent sound notifications for tuition bookings, cancellations, and reschedules',
              sound: 'tuition_bell',
              importance: 5,
              visibility: 1,
              vibration: true
            });
            await PushNotifications.createChannel({
              id: 'custom_alerts',
              name: 'Budlee Premium',
              description: 'Premium notifications with custom chime',
              sound: 'budlee_chime',
              importance: 5,
              visibility: 1,
              vibration: true
            });
          } catch (e) {
            console.warn('[useNotifications] Failed to create Android channels:', e);
          }
        }

        await PushNotifications.addListener('registration', async (token) => {
          // Fix for "2000 sleep issue": We need to wait for FCM to be ready, but 2s is arbitrary.
          // We use a shorter initial sleep and let getFCMTokenWithRetry handle the rest.
          await sleep(1000);
          try {
            const fcmToken = await getFCMTokenWithRetry(FCM);
            if (fcmToken) {
              // Extra safety: If the token is identical to the native token on iOS, it might be the wrong one
              if (Capacitor.getPlatform() === 'ios' && fcmToken === token.value && fcmToken.length < 100) {
                console.warn('[useNotifications] Received native token instead of FCM token, retrying...');
                await sleep(2000);
                const secondTry = await getFCMTokenWithRetry(FCM, 3, 2000);
                if (secondTry) register(secondTry);
                return;
              }
              register(fcmToken);
            }
          } catch (err) {
            console.error('[useNotifications] Token failure:', err);
          }
        });

        const register = async (token) => {
          localStorage.setItem('fcm_token', token);
          const { getBrowserFingerprint } = await import('@/utils/auth');
          const fingerprint = await getBrowserFingerprint();
          await registerPushToken(token, Capacitor.getPlatform(), fingerprint);
          lastRegisteredUser = contextRef.current.userId;
          localStorage.setItem('last_registered_session_token', localStorage.getItem(`${portal}_access_token`));
        };

        await PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('[Notifications] 🔔 Notification received in foreground!', notification);
          const data = notification?.data || {};
          if (data.fcm_log_id) {
            reportFCMReceipt(data.fcm_log_id, 'delivered', Capacitor.getPlatform());
          }
          if (contextRef.current.active) {
            if (isTuitionNotification(data)) {
              soundService?.playTuitionBellSound();
            } else {
              soundService?.playNotificationSound();
            }
            fetchUnreadCount();
          }
        });

        await PushNotifications.addListener('pushNotificationActionPerformed', async (action) => {
          const data = action.notification?.data || {};
          if (data.fcm_log_id) {
            reportFCMReceipt(data.fcm_log_id, 'read', Capacitor.getPlatform());
          }
          const targetUrl = data?.url || data?.link;
          if (targetUrl) {
            const { resolveNotificationLink } = await import('@/utils/linkResolver');
            resolveNotificationLink(targetUrl, navigate);
          }
        });

        if (perm?.receive === 'prompt') {
          // Verify 3-day logic for soft prompt (scoped by portal & userId)
          const declineKey = getDeclineKey(portal, currentState.userId);
          const promptTimeKey = getPromptTimeKey(portal, currentState.userId);
          const hasDeclined = localStorage.getItem(declineKey) || localStorage.getItem('notification_soft_prompt_declined');
          const lastPromptTime = localStorage.getItem(promptTimeKey) || localStorage.getItem('notification_soft_prompt_time');
          const now = Date.now();
          const threeDays = 3 * 24 * 60 * 60 * 1000;

          if (!hasDeclined || (now - parseInt(lastPromptTime || '0')) > threeDays) {
            const promptDelay = isTeacherFlow ? 2500 : 5000;
            await sleep(promptDelay);
            // FINAL check before showing modal: ensure we still have selection and are authed
            if (contextRef.current.authed && (isTeacherFlow || !isStudentFlow || contextRef.current.hasSelection)) {
              setShowConsentModal(true);
            }
          }
          isPushInitialized = true;
          lastHasSelection = contextRef.current.hasSelection;
        } else if (perm?.receive === 'granted') {
          await PushNotifications.register();
          fetchUnreadCount();
          isPushInitialized = true;
          lastHasSelection = contextRef.current.hasSelection;
        } else {
          isPushInitialized = true;
          lastHasSelection = contextRef.current.hasSelection;
        }
      } catch (err) {
        isPushInitialized = false;
      } finally {
        isSetupInProgress = false;
      }
    };

    // Delay registration for clean UX
    const timer = setTimeout(setup, 1000);
    return () => clearTimeout(timer);
  }, [hasSelection, isPrimary, authed, userId, navigate]);

  const confirmConsent = async () => {
    try {
      setShowConsentModal(false);
      const declineKey = getDeclineKey(portal, userId);
      const promptTimeKey = getPromptTimeKey(portal, userId);
      localStorage.setItem(promptTimeKey, Date.now().toString());
      localStorage.removeItem(declineKey);
      localStorage.removeItem('notification_soft_prompt_declined');

      if (Capacitor.isNativePlatform()) {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const perm = await PushNotifications.requestPermissions();
        if (perm?.receive === 'granted') {
          await PushNotifications.register();
        } else {
          isPushInitialized = false;
        }
      } else if (typeof window !== 'undefined' && 'Notification' in window) {
        const webPerm = await Notification.requestPermission();
        if (webPerm === 'granted') {
          const { requestForToken } = await import('../lib/firebase');
          const token = await requestForToken();
          if (token) {
            localStorage.setItem('fcm_token', token);
            const { getBrowserFingerprint } = await import('@/utils/auth');
            const fingerprint = await getBrowserFingerprint();
            await registerPushToken(token, 'web', fingerprint);
            lastRegisteredUser = userId;
            localStorage.setItem('last_registered_session_token', localStorage.getItem(`${portal}_access_token`));
          }
        } else {
          isPushInitialized = false;
        }
      }
    } catch (err) {
      isPushInitialized = false;
    }
  };

  const cancelConsent = () => {
    setShowConsentModal(false);
    const declineKey = getDeclineKey(portal, userId);
    const promptTimeKey = getPromptTimeKey(portal, userId);
    localStorage.setItem(promptTimeKey, Date.now().toString());
    localStorage.setItem(declineKey, 'true');
    isPushInitialized = false;
  };

  return {
    unreadCount,
    fetchUnreadCount,
    refreshUnreadCount: fetchUnreadCount,
    setActive,
    showConsentModal,
    setShowConsentModal,
    confirmConsent,
    cancelConsent
  };
};






